import { useState, useEffect, useCallback } from 'react'
import { useAuthStore } from '../../auth/useAuthStore'
import { apiUrl, authFetch } from '../../lib/api'

interface UsageDay { date: string; requests: number; tokens_in: number; tokens_out: number }

export function BillingDashboard() {
  const user = useAuthStore(s => s.user)
  const [usage, setUsage] = useState<UsageDay[]>([])
  const [totalRequests, setTotalRequests] = useState(0)
  const [totalTokens, setTotalTokens] = useState(0)

  const load = useCallback(() => {
    // Load AI usage for last 30 days
    authFetch(apiUrl('/api/profile')).then(r => r.json()).catch(() => {})
    // Simulate usage data from ai_usage table
    const days: UsageDay[] = []
    for (let i = 29; i >= 0; i--) {
      const d = new Date(); d.setDate(d.getDate() - i)
      days.push({
        date: d.toISOString().split('T')[0],
        requests: Math.floor(Math.random() * 50),
        tokens_in: Math.floor(Math.random() * 10000),
        tokens_out: Math.floor(Math.random() * 5000),
      })
    }
    setUsage(days)
    setTotalRequests(days.reduce((s, d) => s + d.requests, 0))
    setTotalTokens(days.reduce((s, d) => s + d.tokens_in + d.tokens_out, 0))
  }, [])

  useEffect(() => { load() }, [load])

  const plan = user?.plan ?? 'free'
  const limits: Record<string, { requests: number; storage: string }> = {
    free: { requests: 50, storage: '500 MB' },
    pro: { requests: 500, storage: '5 GB' },
    unlimited: { requests: 99999, storage: '50 GB' },
    enterprise: { requests: 99999, storage: '100 GB' },
  }
  const planLimits = limits[plan] ?? limits.free
  const maxReq = Math.max(...usage.map(d => d.requests), 1)

  return (
    <div style={{ height: '100%', overflow: 'auto', padding: 20 }}>
      <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 16, color: 'var(--text-0)', marginBottom: 16 }}>
        💳 Billing & Usage
      </div>

      {/* Plan card */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 20 }}>
        <StatCard label="Current Plan" value={plan.toUpperCase()} accent />
        <StatCard label="AI Requests (30d)" value={`${totalRequests} / ${planLimits.requests === 99999 ? '∞' : planLimits.requests}`} />
        <StatCard label="Tokens Used (30d)" value={`${(totalTokens / 1000).toFixed(1)}K`} />
        <StatCard label="Storage" value={planLimits.storage} />
      </div>

      {/* Usage chart */}
      <div style={{ background: 'var(--bg-1)', border: '1px solid var(--border)', borderRadius: 10, padding: 16, marginBottom: 20 }}>
        <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 11, color: 'var(--text-0)', marginBottom: 12 }}>
          AI Requests — Last 30 Days
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 100 }}>
          {usage.map((d, i) => (
            <div key={i} title={`${d.date}: ${d.requests} requests`} style={{
              flex: 1, borderRadius: '3px 3px 0 0', minWidth: 4,
              height: `${(d.requests / maxReq) * 100}%`,
              background: d.requests > planLimits.requests / 30 ? 'var(--red)' : 'var(--accent)',
              opacity: 0.7, transition: 'height 0.3s',
            }} />
          ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
          <span style={{ fontSize: 8, color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>30 days ago</span>
          <span style={{ fontSize: 8, color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>Today</span>
        </div>
      </div>

      {/* Token breakdown */}
      <div style={{ background: 'var(--bg-1)', border: '1px solid var(--border)', borderRadius: 10, padding: 16, marginBottom: 20 }}>
        <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 11, color: 'var(--text-0)', marginBottom: 12 }}>
          Token Breakdown
        </div>
        <div style={{ display: 'flex', gap: 20 }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--accent)', fontFamily: 'var(--font-mono)' }}>
              {(usage.reduce((s, d) => s + d.tokens_in, 0) / 1000).toFixed(1)}K
            </div>
            <div style={{ fontSize: 9, color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>Input tokens</div>
          </div>
          <div>
            <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--accent-2)', fontFamily: 'var(--font-mono)' }}>
              {(usage.reduce((s, d) => s + d.tokens_out, 0) / 1000).toFixed(1)}K
            </div>
            <div style={{ fontSize: 9, color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>Output tokens</div>
          </div>
        </div>
      </div>

      {/* Upgrade CTA */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(124,110,245,0.1), rgba(95,212,164,0.1))',
        border: '1px solid rgba(124,110,245,0.3)', borderRadius: 10, padding: 16,
      }}>
        <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 13, color: 'var(--text-0)', marginBottom: 12 }}>
          {plan === 'enterprise' ? 'Your Plan' : 'Upgrade Your Plan'}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
          <PlanButton current={plan} target="free" price="$0/mo" desc="50 AI req/day · 500 MB" />
          <PlanButton current={plan} target="pro" price="$20/mo" desc="500 AI req/day · 5 GB" />
          <PlanButton current={plan} target="unlimited" price="$49/mo" desc="Unlimited AI · 50 GB" />
          <PlanButton current={plan} target="enterprise" price="$99/mo" desc="SSO · Audit · Admin" />
        </div>
      </div>
    </div>
  )
}

function StatCard({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div style={{
      background: 'var(--bg-1)', border: '1px solid var(--border)', borderRadius: 10, padding: 14,
    }}>
      <div style={{ fontSize: 9, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', marginBottom: 4 }}>{label}</div>
      <div style={{
        fontSize: 18, fontWeight: 700, fontFamily: 'var(--font-mono)',
        color: accent ? 'var(--accent)' : 'var(--text-0)',
      }}>{value}</div>
    </div>
  )
}

function PlanButton({ current, target, price, desc }: { current: string; target: string; price: string; desc: string }) {
  const upgradePlan = useAuthStore(s => s.upgradePlan)
  const isCurrent = current === target
  const isDowngrade = ['enterprise', 'unlimited', 'pro', 'free'].indexOf(current) >= ['enterprise', 'unlimited', 'pro', 'free'].indexOf(target)

  const handleUpgrade = async () => {
    if (isCurrent) return
    if (target === 'free') return
    try {
      const res = await authFetch(apiUrl('/api/stripe/checkout'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan: target }),
      })
      const data = await res.json()
      if (data.url) window.location.href = data.url
      else alert(data.error || 'Checkout failed')
    } catch { alert('Payment service unavailable') }
  }

  return (
    <div style={{ background: 'var(--bg-1)', border: `1px solid ${isCurrent ? 'var(--accent)' : 'var(--border)'}`, borderRadius: 8, padding: 12, textAlign: 'center' }}>
      <div style={{ fontSize: 12, fontWeight: 700, fontFamily: 'var(--font-mono)', color: 'var(--text-0)', textTransform: 'capitalize', marginBottom: 2 }}>{target}</div>
      <div style={{ fontSize: 14, fontWeight: 900, color: 'var(--accent)', fontFamily: 'var(--font-display)', marginBottom: 4 }}>{price}</div>
      <div style={{ fontSize: 9, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', marginBottom: 8 }}>{desc}</div>
      <button onClick={handleUpgrade} disabled={isCurrent} style={{
        width: '100%', padding: '6px', borderRadius: 6, fontSize: 10, fontFamily: 'var(--font-mono)', fontWeight: 700, cursor: isCurrent ? 'default' : 'pointer',
        border: isCurrent ? '1px solid var(--accent)' : '1px solid var(--border-2)',
        background: isCurrent ? 'rgba(124,110,245,0.1)' : 'none',
        color: isCurrent ? 'var(--accent)' : 'var(--text-1)',
      }}>
        {isCurrent ? 'Current' : isDowngrade ? 'Downgrade' : 'Upgrade'}
      </button>
    </div>
  )
}
